import { getToken, deleteToken, onMessage } from 'firebase/messaging';
import { firebaseConfig, getMessagingIfSupported } from './firebase';
import { apiFetch } from './api-client';
import { logger } from './logger';
import { queryClient } from './query-client';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? '';

/**
 * The messaging worker gets its OWN scope, and must.
 *
 * vite-plugin-pwa's `registerSW.js` registers the Workbox worker at
 * scope `/` on every page load, and a scope holds exactly one
 * registration — so registering the messaging worker at `/` as well did
 * not add a second worker, it REPLACED the PWA one. Verified in headless
 * Chrome: after that call the only registration left was
 * `firebase-messaging-sw.js`, precache and offline shell gone, and the
 * next page load registered `sw.js` again and evicted the messaging
 * worker in turn. The two alternated, so on any given load exactly one
 * of "the app works offline" and "background push arrives" was true.
 *
 * `/firebase-cloud-messaging-push-scope` is the scope the Firebase SDK
 * itself picks when it registers the worker unaided, so nothing here is
 * novel — it is just made explicit, because we need the `?cfg=` on the
 * URL and the SDK would register the bare path.
 */
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

/**
 * The messaging worker's script URL, config and all.
 *
 * `public/firebase-messaging-sw.js` is plain JS outside the bundle: it
 * cannot read `import.meta.env`, and a committed per-env config would
 * make the staging deploy ship production's project id (CLAUDE.md §6).
 * It used to fetch `/__fcm-config.json` instead — but a worker is
 * terminated when idle and re-evaluated for essentially every push, and
 * a listener registered after that push has begun dispatching does not
 * receive it. So `onBackgroundMessage` fired for approximately no push
 * ever sent to a citizen, and the browser showed its generic "updated in
 * the background" notice in place of ours.
 *
 * `self.location.search` is readable synchronously, before any push can
 * arrive, so the config rides on the URL. The query string is part of
 * the script's identity: change environments and the browser installs a
 * different worker rather than keeping the old config, which is what we
 * want.
 */
function serviceWorkerUrl(): string {
  return `/firebase-messaging-sw.js?cfg=${encodeURIComponent(JSON.stringify(firebaseConfig))}`;
}

// registerFcmToken() now runs more than once per session (sign-in, then
// FcmTenantSync on every resolved-city change). Token registration is
// idempotent server-side, but onMessage() STACKS listeners — each call
// would add another copy of the invalidation fan-out. Wire it once.
let foregroundWired = false;

/** How long to wait for a freshly-registered worker to activate. */
const SW_ACTIVATION_TIMEOUT_MS = 10_000;

/**
 * Wait for a service-worker registration to actually become ACTIVE.
 *
 * This exists because of a sharp edge in the Firebase SDK. `getToken()`
 * eventually calls `swRegistration.pushManager.subscribe(...)`, which
 * REJECTS while the registration has no active worker
 * ("AbortError: Registration failed - no active Service Worker"). The
 * SDK guards against that with its own `waitForRegistrationActive()` —
 * but only on the path where the SDK registers the worker itself. Pass
 * your own `serviceWorkerRegistration` and you opt straight out of the
 * wait, which is exactly what we do.
 *
 * `navigator.serviceWorker.register()` resolves as soon as the
 * registration EXISTS. On the first enable in a browser that has never
 * installed this worker, it comes back with `installing` set and
 * `active === null`, so the very next line raced it and lost. The
 * operator saw "Could not turn notifications on. Try again, or reload
 * the page." — and a reload genuinely did fix it, because by then the
 * worker had installed and claimed the page. The error was truthful
 * (no token was registered on that click) but entirely recoverable.
 *
 * NOT `navigator.serviceWorker.ready`: that resolves for whatever
 * registration controls the PAGE, which is a different worker than the
 * one we just registered. Wait on the object we hold.
 */
async function waitForActiveRegistration(
  registration: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration> {
  if (registration.active) return registration;

  const pending = registration.installing ?? registration.waiting;
  if (!pending) {
    // No active worker and nothing on its way — there is nothing to
    // wait for, and subscribing would fail. Say so rather than hanging.
    throw new Error('service worker registration has no active or pending worker');
  }

  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.removeEventListener('statechange', onStateChange);
      // A wedged install must not leave the button spinning forever.
      reject(new Error(`service worker did not activate within ${SW_ACTIVATION_TIMEOUT_MS}ms`));
    }, SW_ACTIVATION_TIMEOUT_MS);

    function onStateChange(): void {
      // `redundant` means this worker was replaced or failed to install;
      // it will never activate, so waiting out the timeout is pointless.
      if (pending!.state === 'activated') {
        clearTimeout(timer);
        pending!.removeEventListener('statechange', onStateChange);
        resolve();
      } else if (pending!.state === 'redundant') {
        clearTimeout(timer);
        pending!.removeEventListener('statechange', onStateChange);
        reject(new Error('service worker became redundant before activating'));
      }
    }

    pending.addEventListener('statechange', onStateChange);
    // It may have activated between the check above and this listener.
    onStateChange();
  });

  return registration;
}

/**
 * Foreground FCM wiring for the citizen PWA.
 *
 * Three responsibilities:
 *  1. Request notification permission + obtain a token via the SDK's
 *     `getToken()`. The token is opaque to us; the api stores it
 *     against the signed-in user and uses it to push status changes.
 *  2. Register the token with `POST /v1/fcm/tokens` (idempotent — the
 *     api deduplicates on insert).
 *  3. Subscribe to foreground messages so a tab that's already in the
 *     foreground still shows the notification (the service worker
 *     handles background pushes; foreground delivery never reaches the
 *     SW). Today we only refetch — the api domain queries (TanStack
 *     Query) drive their own invalidation. Wire `onMessage` to the
 *     query cache later if we want richer UX.
 *
 * Everything is best-effort. Failures are logged and swallowed so that
 * the rest of the app keeps working without notifications.
 */
export async function registerFcmToken(
  tenantId?: string | null,
  opts: { requestPermission?: boolean } = {},
): Promise<string | null> {
  if (!VAPID_KEY) {
    // The VAPID public key comes from the Firebase Console (Cloud
    // Messaging → Web Push certificates). Until it's set in the
    // VITE_FIREBASE_VAPID_KEY env var, web push is intentionally a
    // no-op — the service worker still installs, but no token is
    // requested and nothing routes back to the citizen.
    logger.warn('FCM registration skipped', { reason: 'no-vapid-key' });
    return null;
  }
  try {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      logger.info('FCM registration skipped', { reason: 'no-service-worker' });
      return null;
    }
    // `Notification` does not exist in an iOS Safari TAB — the Push API is
    // only exposed to a PWA installed to the Home Screen (iOS 16.4+).
    // Dereferencing the bare global threw a ReferenceError straight into
    // the catch below, whose only diagnostic was a DEV-gated console.warn:
    // in production, on the one platform where it always happens, the
    // failure was completely invisible at BOTH ends (the api logs nothing
    // for a zero-token send either). Make it a deliberate branch.
    if (typeof Notification === 'undefined') {
      logger.info('FCM registration skipped', {
        reason: 'notification-api-unavailable',
        hint: 'iOS requires the PWA to be installed to the Home Screen',
      });
      return null;
    }
    if (Notification.permission === 'denied') {
      logger.info('FCM registration skipped', { reason: 'permission-denied' });
      return null;
    }
    if (Notification.permission === 'default') {
      // Safari/iOS (and Firefox) only honour requestPermission() inside a
      // user gesture — called from a mount effect it is rejected outright,
      // which meant web push could NEVER be enabled there. Auto-registration
      // therefore stops here; the Profile toggle passes requestPermission
      // so the prompt happens on a real tap.
      if (!opts.requestPermission) return null;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return null;
    }
    const messaging = await getMessagingIfSupported();
    if (!messaging) {
      // The SDK's own isSupported() — false for a non-standalone iOS
      // Safari tab, and for browsers without the Push API.
      logger.info('FCM registration skipped', { reason: 'messaging-unsupported' });
      return null;
    }

    // Its own scope, alongside the Workbox worker at `/` — never
    // instead of it. See FCM_SW_SCOPE.
    const reg = await waitForActiveRegistration(
      await navigator.serviceWorker.register(serviceWorkerUrl(), {
        scope: FCM_SW_SCOPE,
      }),
    );
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg,
    });
    if (!token) return null;

    // tenantId = the citizen's SELECTED city — the api subscribes this
    // device to that tenant's announcement/emergency FCM topics (citizens
    // have no server-side tenant, so topic membership is keyed per token).
    await apiFetch('/fcm/tokens', {
      method: 'POST',
      body: { token, platform: 'web', ...(tenantId ? { tenantId } : {}) },
    });

    // Surface foreground pushes. Foreground delivery never reaches the
    // service worker, so an open tab would otherwise show stale data —
    // invalidate the notification inbox + issue queries so screens
    // refetch on the next render. Subscribed once per session (guard
    // above) — re-registration must not stack duplicate listeners.
    if (foregroundWired) return token;
    // Claim the guard synchronously BEFORE any await — two near-
    // simultaneous registrations must not both pass the check and wire
    // onMessage twice.
    foregroundWired = true;
    onMessage(messaging, (payload) => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['my-issues'] });
      // Prefix-matches every ['issue', id] detail query — a 'report'
      // push (status change OR staff thread reply) live-updates an open
      // report detail screen.
      void queryClient.invalidateQueries({ queryKey: ['issue'] });
      void queryClient.invalidateQueries({ queryKey: ['issues-map'] });
      // Staff replies in the report ticketing thread arrive as
      // type:'report' with id=issueId — refresh the open thread too.
      if (payload.data?.type === 'report' && payload.data.id) {
        void queryClient.invalidateQueries({ queryKey: ['issue-messages', payload.data.id] });
      }
      // Staff replied while the user has the thread open — foreground
      // pushes never reach the SW, so refresh the support queries here.
      if (payload.data?.type === 'support') {
        void queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
        if (payload.data.id) {
          void queryClient.invalidateQueries({ queryKey: ['support-ticket', payload.data.id] });
        }
      }
    });

    return token;
  } catch (err) {
    // Push failures must never break sign-in — but "must not throw" is not
    // "must not be recorded". This used to log only under import.meta.env
    // .DEV, so every production failure was silent.
    logger.warn('FCM registration failed', {
      reason: 'unexpected-error',
      error: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
}

export async function unregisterFcmToken(): Promise<void> {
  if (!VAPID_KEY) return;
  try {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    // Nothing to drop if push was never enabled — and checking spares a
    // getToken() that would throw on every sign-out of every citizen who
    // declined notifications, drowning the real failures below.
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const messaging = await getMessagingIfSupported();
    if (!messaging) return;
    // Hand getToken the registration we already made. Left to itself it
    // registers `/firebase-messaging-sw.js` with NO query string under
    // FCM_SW_SCOPE — which is OUR scope, so it would replace the
    // configured worker with a config-less copy, at sign-out of all
    // moments, and the next citizen to sign in on this device would get
    // the generic browser notice again.
    const registration = (await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE)) ?? undefined;
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      ...(registration ? { serviceWorkerRegistration: registration } : {}),
    }).catch(() => null);
    if (token) {
      // Best-effort: tell the api first, then drop locally.
      await apiFetch(`/fcm/tokens/${encodeURIComponent(token)}`, {
        method: 'DELETE',
      }).catch((err: unknown) =>
        logger.warn('Dropping the FCM token on the server failed', {
          error: err instanceof Error ? err.message : String(err),
        }),
      );
    }
    await deleteToken(messaging).catch(() => undefined);
  } catch (err) {
    // Sign-out proceeds regardless — but "must not throw" is not "must
    // not be recorded".
    logger.warn('FCM unregistration failed', {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
